import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { Button } from '@peacecover/component';
import { useGetBettieDailyCheckInStatus } from '@pocapan/shared/domains/bettie/ui/hooks/useBettieDailyCheckInStatus';
import { useClaimBettieDailyCheckInReward } from '@pocapan/shared/domains/bettie/ui/hooks/useClaimBettieDailyCheckInReward';
import { useFeatureEnabled } from '@pocapan/shared/domains/features/ui/hooks/useFeatureEnabled';
import { FEATURE_KEYS } from '@pocapan/shared/domains/features/constants';

const BettieFreeChargeSection = () => {
  const [isCheckInPopupOpen, setIsCheckInPopupOpen] = useState(false);
  const navigate = useNavigate();
  const { data: bettieDailyCheckInStatus, isLoading } = useGetBettieDailyCheckInStatus();
  const { mutateAsync: claimBettieDailyCheckInReward } = useClaimBettieDailyCheckInReward();
  const { data, isLoading: isBettieRewardsLoading } = useFeatureEnabled(
    FEATURE_KEYS.BETTIE_REWARDS
  );

  const isBettieRewardsEnabled = data?.featureFlag.enabled;

  useEffect(() => {
    if (!isCheckInPopupOpen) {
      return;
    }
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [isCheckInPopupOpen]);

  if (isLoading || isBettieRewardsLoading) {
    return (
      <div className="h-[72px] w-full animate-pulse rounded-[20px] bg-pure-white shadow-[0_0_3px_rgba(0,0,0,0.02),_0_0_13px_rgba(0,0,0,0.03)] dark:bg-background-elevated" />
    );
  }

  const handleCheckIn = async () => {
    try {
      const response = await claimBettieDailyCheckInReward();
      if (response.bettieTransactions) {
        setIsCheckInPopupOpen(true);
      }
    } catch {
      return;
    }
  };
  const isDisabled = !isBettieRewardsEnabled && !!bettieDailyCheckInStatus;
  const handleFreeChargeClick = () => {
    if (isBettieRewardsEnabled) {
      navigate('/free-charge');
      return;
    }
    if (!bettieDailyCheckInStatus) {
      handleCheckIn();
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={isDisabled}
        onClick={handleFreeChargeClick}
        className="flex w-full items-center justify-between rounded-[20px] bg-pure-white px-[18px] py-[12px] text-left shadow-[0_0_3px_rgba(0,0,0,0.02),_0_0_13px_rgba(0,0,0,0.03)] disabled:cursor-default dark:bg-background-elevated"
      >
        <div className="flex min-w-0 flex-col gap-[4px]">
          <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv2">
            {isDisabled ? t('BettieChatList.AlreadyGetFreeCharge') : t('BettieChatList.freeCharge')}
          </p>
          <p className="truncate text-[14px] font-normal leading-[17px] text-text-main-lv3">
            {t('BettieChatList.freeChargeDescription', { maxCount: '1,000' })}
          </p>
        </div>
        <img
          src="/assets/bettie/free_charge_gift.png"
          alt=""
          className="size-[48px] shrink-0 object-contain"
        />
      </button>
      <AnimatePresence>
        {isCheckInPopupOpen && (
          <motion.div
            className="fixed top-0 left-0 w-full h-full bg-black/50 z-[999] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-[311px] px-[14px] pt-[32px] pb-[16px] flex flex-col items-center gap-[32px] bg-background-light dark:bg-background-dark rounded-[24px]"
              initial={{ scale: 0.95, opacity: 0, y: 8 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 8 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24, duration: 0.2 }}
            >
              <img
                src="/assets/icons/check_calendar.png"
                alt="free_charge_gift"
                className="w-[145px] h-[130px]"
              />
              <div className="flex flex-col items-center gap-[8px]">
                <p className="text-[17px] font-bold leading-[20px] text-text-main-lv1">
                  {t('BettieChatList.completeAttendanceCheck')}
                </p>
                <p className="text-[15px] leading-[18px] text-text-main-lv4 text-center whitespace-pre-line">
                  {t('BettieChatList.waitingForFreeCharge')}
                </p>
              </div>
              <Button
                size="large"
                className="text-[17px] font-semibold w-full"
                onClick={() => setIsCheckInPopupOpen(false)}
              >
                {t('confirm')}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BettieFreeChargeSection;
